import { useState } from 'react';
import {
  Box,
  Button,
  Collapse,
  IconButton,
  Tooltip,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useTranslation } from 'react-i18next';
import type { InvoiceLineItem, InvoicePreviewLine } from '@/hooks/useInvoices';
import { groupInvoiceLines } from '@/lib/invoiceLineGroups';
import { calculateInvoiceFromLines } from '@/utils/money';
import { BillingLineRow } from './billing/BillingLineRow';
import type { AnnotatedBillingLine } from './billing/billingLineSource';

type GroupedLine = (InvoiceLineItem | InvoicePreviewLine) & {
  source?: AnnotatedBillingLine['source'];
};

function lineGrossCentsByLine(
  lines: readonly GroupedLine[],
  fallbackTaxPercent: number,
): Map<GroupedLine, number> {
  const allocated = calculateInvoiceFromLines(
    lines.map((line) => ({
      unitPriceCents: line.unitPriceCents,
      quantity: line.quantity,
      discountPercent: line.discountPercent,
      taxable: line.taxable,
      taxPercent: line.taxable ? (line.taxPercent ?? fallbackTaxPercent) : 0,
    })),
  ).lineGrossCents;
  const byLine = new Map<GroupedLine, number>();
  lines.forEach((line, index) => {
    byLine.set(line, allocated[index] ?? 0);
  });
  return byLine;
}

function groupTotalGrossCents(
  lines: readonly GroupedLine[],
  grossByLine: Map<GroupedLine, number>,
): number {
  let total = 0;
  for (const line of lines) {
    total += grossByLine.get(line) ?? 0;
  }
  return total;
}

interface GroupedInvoiceLinesProps {
  lines: readonly GroupedLine[];
  formatPrice: (cents: number) => string;
  emptyLabel?: string;
  /** Label of the call to action under an empty list; shown with `onAddFirstLine` (#4117). */
  emptyAddLabel?: string;
  /** Adds the first line from the empty state, without edit mode being on first. */
  onAddFirstLine?: () => void;
  fallbackTaxPercent?: number;
  editable?: boolean;
  saving?: boolean;
  onCommitQuantity?: (line: AnnotatedBillingLine, quantity: number) => void;
  onCommitUnitPrice?: (line: AnnotatedBillingLine, unitPriceGrossCents: number) => void;
  onCommitDiscount?: (line: AnnotatedBillingLine, discountPercent: number) => void;
  onDeleteLine?: (line: AnnotatedBillingLine) => void;
  /** `groupSortOrder` tells apart two works of the same product (#3655). */
  onAddLine?: (groupName: string | null, groupSortOrder?: number) => void;
}

export function GroupedInvoiceLines({
  lines,
  formatPrice,
  emptyLabel,
  emptyAddLabel,
  onAddFirstLine,
  fallbackTaxPercent = 0,
  editable = false,
  saving = false,
  onCommitQuantity,
  onCommitUnitPrice,
  onCommitDiscount,
  onDeleteLine,
  onAddLine,
}: GroupedInvoiceLinesProps) {
  const { t: tc } = useTranslation('common');
  const { t: ti } = useTranslation('invoices');
  const groups = groupInvoiceLines(lines, ti('print.otherLinesGroup'));
  const grossByLine = lineGrossCentsByLine(lines, fallbackTaxPercent);
  const defaultExpanded = groups.length <= 3;
  const [expandedByKey, setExpandedByKey] = useState<Record<string, boolean>>({});

  const isExpanded = (key: string): boolean => expandedByKey[key] ?? defaultExpanded;
  const toggleGroup = (key: string) => {
    setExpandedByKey((prev) => ({ ...prev, [key]: !(prev[key] ?? defaultExpanded) }));
  };

  if (groups.length === 0) {
    return (
      <Box>
        <Typography variant="body2" color="text.secondary">
          {emptyLabel ?? ti('generate.previewEmpty')}
        </Typography>
        {editable && onAddLine && (
          <Button
            size="small"
            startIcon={<AddIcon />}
            onClick={() => onAddLine(null)}
            disabled={saving}
            sx={{ mt: 1 }}
            data-testid="billing-add-line-outside"
          >
            {ti('edit.addLine')}
          </Button>
        )}
        {!editable && onAddFirstLine && (
          <Button
            size="small"
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={onAddFirstLine}
            disabled={saving}
            sx={{ mt: 1 }}
            data-testid="billing-empty-add-line"
          >
            {emptyAddLabel ?? ti('edit.addLine')}
          </Button>
        )}
      </Box>
    );
  }

  return (
    <Stack spacing={2} data-testid="grouped-invoice-lines">
      {groups.map((group) => {
        const groupTotal = groupTotalGrossCents(group.lines, grossByLine);
        const expanded = isExpanded(group.key);
        return (
          <Paper
            key={group.key}
            variant="outlined"
            data-testid="invoice-line-group"
            data-group-name={group.name}
            sx={{ p: 2 }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                cursor: 'pointer',
              }}
              onClick={() => toggleGroup(group.key)}
            >
              <Tooltip title={expanded ? ti('workOrderBilling.collapseGroup') : ti('workOrderBilling.expandGroup')}>
                <IconButton
                  size="small"
                  aria-label={expanded ? ti('workOrderBilling.collapseGroup') : ti('workOrderBilling.expandGroup')}
                  aria-expanded={expanded}
                  onClick={(event) => {
                    event.stopPropagation();
                    toggleGroup(group.key);
                  }}
                >
                  {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                </IconButton>
              </Tooltip>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, flex: 1, minWidth: 0 }}>
                {group.name}
              </Typography>
              <Typography
                variant="subtitle2"
                fontWeight={700}
                sx={{ whiteSpace: 'nowrap' }}
                data-testid="invoice-line-group-total"
                data-gross-cents={groupTotal}
                aria-label={`${ti('workOrderBilling.groupTotal')} ${formatPrice(groupTotal)}`}
              >
                {formatPrice(groupTotal)}
              </Typography>
            </Box>
            {group.lines[0]?.groupNotes && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mt: 1, whiteSpace: 'pre-line', overflowWrap: 'anywhere' }}
                data-testid="invoice-line-group-notes"
              >
                {group.lines[0].groupNotes}
              </Typography>
            )}
            <Collapse in={expanded}>
              <Stack spacing={2} sx={{ mt: 1.5 }}>
                {group.lines.map((line, index) => {
                  const annotated = line as AnnotatedBillingLine;
                  return (
                    <BillingLineRow
                      key={'id' in line ? line.id : `${group.key}-${line.lineType}-${index}`}
                      line={annotated}
                      formatPrice={formatPrice}
                      lineGrossCents={grossByLine.get(line) ?? 0}
                      fallbackTaxPercent={fallbackTaxPercent}
                      unitLabel={tc(`units.${line.unit || 'piece'}`)}
                      editable={editable}
                      saving={saving}
                      onCommitQuantity={(next, quantity) => onCommitQuantity?.(next, quantity)}
                      onCommitUnitPrice={(next, cents) => onCommitUnitPrice?.(next, cents)}
                      onCommitDiscount={(next, percent) => onCommitDiscount?.(next, percent)}
                      onDelete={(next) => onDeleteLine?.(next)}
                    />
                  );
                })}
                {/* A fixed-price package takes no extra rows; extra work is a separate service (#3655). */}
                {editable && onAddLine && !group.lines.some((line) => line.source?.kind === 'fixedService') && (
                  <Box>
                    <Button
                      size="small"
                      startIcon={<AddIcon />}
                      onClick={() => onAddLine(group.name, group.sortOrder)}
                      disabled={saving}
                      data-testid="billing-add-line-group"
                    >
                      {ti('edit.addLine')}
                    </Button>
                  </Box>
                )}
              </Stack>
            </Collapse>
          </Paper>
        );
      })}
      {editable && onAddLine && (
        <Box>
          <Button
            size="small"
            startIcon={<AddIcon />}
            onClick={() => onAddLine(null)}
            disabled={saving}
            data-testid="billing-add-line-outside"
          >
            {ti('edit.addLine')}
          </Button>
        </Box>
      )}
    </Stack>
  );
}
